export interface ProjectMediaRetirementReceipt {
    fileMetadataId: string;
    s3Key: string;
}

export interface ProjectMediaRetirementOutcome {
    retired?: unknown;
    alreadyComplete?: unknown;
    already_complete?: unknown;
    stillReferenced?: unknown;
    still_referenced?: unknown;
    unresolved?: unknown;
}

export type ProjectMediaRetirementQueue = Map<
    string,
    Map<string, ProjectMediaRetirementReceipt>
>;

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const MEDIA_METADATA_ID_FIELDS = [
    'fileMetadataId', 'file_metadata_id', 'sourceFileMetadataId', 'source_file_metadata_id',
];
const MEDIA_S3_KEY_FIELDS = [
    's3Key', 's3_key', 'fileS3Key', 'file_s3_key', 'cloudObjectKey', 'cloud_object_key',
    'pdfS3Key', 'pdf_s3_key', 'sourcePdfS3Key', 'source_pdf_s3_key',
];
const MEDIA_URL_FIELDS = [
    'url', 'src', 'data', 'dataUrl', 'data_url', 'cleanData', 'clean_data',
    'originalData', 'original_data', 'previewUrl', 'preview_url',
    'thumbnailUrl', 'thumbnail_url', 's3Url', 's3_url',
];

function exactStringValues(item: unknown, fields: string[]): Set<string> {
    if (!item || typeof item !== 'object') return new Set();
    const record = item as Record<string, unknown>;
    return new Set(fields
        .map(field => record[field])
        .filter((value): value is string => typeof value === 'string' && value.length > 0));
}

function setsIntersect(left: Set<string>, right: Set<string>): boolean {
    for (const value of left) if (right.has(value)) return true;
    return false;
}

/** Exact alias equality only; never infer object identity by filename or URL parsing. */
function isExactRemovedMediaAlias(candidate: unknown, removed: unknown): boolean {
    if (!candidate || !removed) return false;
    if (candidate === removed) return true;
    const removedKeys = exactStringValues(removed, MEDIA_S3_KEY_FIELDS);
    const removedUrls = exactStringValues(removed, MEDIA_URL_FIELDS);
    if (typeof candidate === 'string') {
        return removedKeys.has(candidate) || removedUrls.has(candidate);
    }
    if (typeof candidate !== 'object' || typeof removed !== 'object') return false;
    const candidateRecord = candidate as Record<string, unknown>;
    const removedRecord = removed as Record<string, unknown>;
    if (candidateRecord.id != null
        && removedRecord.id != null
        && String(candidateRecord.id) === String(removedRecord.id)) return true;
    return setsIntersect(
        exactStringValues(candidate, MEDIA_METADATA_ID_FIELDS),
        exactStringValues(removed, MEDIA_METADATA_ID_FIELDS),
    ) || setsIntersect(exactStringValues(candidate, MEDIA_S3_KEY_FIELDS), removedKeys)
        || setsIntersect(exactStringValues(candidate, MEDIA_URL_FIELDS), removedUrls);
}

export function clearRetiredProjectSelectedHero(currentHero: unknown, removed: unknown): unknown {
    return isExactRemovedMediaAlias(currentHero, removed) ? null : currentHero;
}

export function clearRetiredProjectPhotoAliases(current: unknown, removed: unknown): unknown {
    if (!Array.isArray(current)) return current;
    const filtered = current.filter(candidate => !isExactRemovedMediaAlias(candidate, removed));
    return filtered.length === current.length ? current : filtered;
}

export function clearRetiredProjectFormDataPhotoAliases(current: any, removed: unknown): any {
    if (!current || typeof current !== 'object' || !Array.isArray(current.photos)) return current;
    const photos = clearRetiredProjectPhotoAliases(current.photos, removed);
    return photos === current.photos ? current : { ...current, photos };
}

export function clearRetiredProjectScopePhotoAliases(current: unknown, removed: unknown): unknown {
    if (!Array.isArray(current)) return current;
    let changed = false;
    const next = current.map(item => {
        if (!item || typeof item !== 'object' || !Array.isArray(item.images)) return item;
        const images = clearRetiredProjectPhotoAliases(item.images, removed);
        if (images === item.images) return item;
        changed = true;
        return { ...item, images };
    });
    return changed ? next : current;
}

export function clearRetiredProjectQuoteHero(quoteData: any, removed: unknown): any {
    if (!quoteData || typeof quoteData !== 'object'
        || !quoteData.header || typeof quoteData.header !== 'object'
        || !isExactRemovedMediaAlias(quoteData.header.heroImage, removed)) {
        return quoteData;
    }
    return {
        ...quoteData,
        header: {
            ...quoteData.header,
            heroImage: '',
            heroImageAutoSelectionSuppressed: true,
        },
    };
}

export function clearRetiredProjectQuotePhotoAliases(quoteData: any, removed: unknown): any {
    if (!quoteData || typeof quoteData !== 'object') return quoteData;
    const withoutHero = clearRetiredProjectQuoteHero(quoteData, removed);
    const scope = clearRetiredProjectScopePhotoAliases(withoutHero.scope, removed);
    return scope === withoutHero.scope ? withoutHero : { ...withoutHero, scope };
}

function exactReceiptCandidate(candidate: any): ProjectMediaRetirementReceipt | null {
    if (!candidate || typeof candidate !== 'object') return null;
    const rawMetadataId = candidate.fileMetadataId
        ?? candidate.file_metadata_id
        ?? candidate.sourceFileMetadataId
        ?? candidate.source_file_metadata_id;
    const rawS3Key = candidate.s3Key
        ?? candidate.s3_key
        ?? candidate.fileS3Key
        ?? candidate.file_s3_key
        ?? candidate.cloudObjectKey
        ?? candidate.cloud_object_key
        ?? candidate.pdfS3Key
        ?? candidate.pdf_s3_key
        ?? candidate.sourcePdfS3Key
        ?? candidate.source_pdf_s3_key;
    if (typeof rawMetadataId !== 'string' || typeof rawS3Key !== 'string') return null;
    const fileMetadataId = rawMetadataId.trim().toLowerCase();
    const s3Key = rawS3Key.trim();
    const tenantPrefix = s3Key.split('/', 1)[0] || '';
    if (!UUID_RE.test(fileMetadataId)
        || !UUID_RE.test(tenantPrefix)
        || !s3Key.startsWith(`${tenantPrefix}/`)
        || s3Key.length > 2048) {
        return null;
    }
    return { fileMetadataId, s3Key };
}

function exactReceipt(item: any): ProjectMediaRetirementReceipt | null {
    if (!item || typeof item !== 'object') return null;
    return exactReceiptCandidate(item)
        || exactReceiptCandidate(item.cloudPlayback);
}

function receiptKey(receipt: ProjectMediaRetirementReceipt): string {
    return `${receipt.fileMetadataId}:${receipt.s3Key}`;
}

function compareExact(left: string, right: string): number {
    return left < right ? -1 : left > right ? 1 : 0;
}

export function createProjectMediaRetirementQueue(): ProjectMediaRetirementQueue {
    return new Map();
}

/** Queue intent only; object deletion remains owned by the later Project save. */
export function queueProjectMediaRetirementReceipt(
    queue: ProjectMediaRetirementQueue,
    projectId: string | null | undefined,
    item: unknown,
): ProjectMediaRetirementReceipt | null {
    if (!UUID_RE.test(String(projectId || ''))) return null;
    const receipt = exactReceipt(item);
    if (!receipt) return null;
    const projectKey = String(projectId).toLowerCase();
    const pending = queue.get(projectKey) || new Map<string, ProjectMediaRetirementReceipt>();
    pending.set(receiptKey(receipt), receipt);
    queue.set(projectKey, pending);
    return receipt;
}

export function pendingProjectMediaRetirementReceipts(
    queue: ProjectMediaRetirementQueue,
    projectId: string | null | undefined,
): ProjectMediaRetirementReceipt[] {
    const pending = queue.get(String(projectId || '').toLowerCase());
    if (!pending) return [];
    return [...pending.values()].sort((left, right) => (
        compareExact(left.s3Key, right.s3Key)
        || compareExact(left.fileMetadataId, right.fileMetadataId)
    ));
}

/**
 * Read only exact positive acknowledgements from one canonical save outcome.
 * Missing/malformed outcomes acknowledge nothing, and a server receipt must
 * also belong to the request snapshot so it cannot consume a later in-flight
 * removal from the same Project queue.
 */
export function confirmedProjectMediaRetirementReceipts(
    outcome: ProjectMediaRetirementOutcome | null | undefined,
    requested: ProjectMediaRetirementReceipt[],
): ProjectMediaRetirementReceipt[] {
    if (!outcome || typeof outcome !== 'object') return [];
    const requestedByKey = new Map<string, ProjectMediaRetirementReceipt>();
    for (const candidate of requested) {
        const receipt = exactReceipt(candidate);
        if (receipt) requestedByKey.set(receiptKey(receipt), receipt);
    }
    const confirmed = new Map<string, ProjectMediaRetirementReceipt>();
    const positiveLists = [
        outcome.retired,
        outcome.alreadyComplete ?? outcome.already_complete,
    ];
    for (const list of positiveLists) {
        if (!Array.isArray(list)) continue;
        for (const candidate of list) {
            const receipt = exactReceipt(candidate);
            if (!receipt) continue;
            const requestedReceipt = requestedByKey.get(receiptKey(receipt));
            if (requestedReceipt) confirmed.set(receiptKey(receipt), requestedReceipt);
        }
    }
    return [...confirmed.values()].sort((left, right) => (
        compareExact(left.s3Key, right.s3Key)
        || compareExact(left.fileMetadataId, right.fileMetadataId)
    ));
}

/** Clear only receipts proven to have travelled with one confirmed save. */
export function acknowledgeProjectMediaRetirementReceipts(
    queue: ProjectMediaRetirementQueue,
    projectId: string | null | undefined,
    acknowledged: ProjectMediaRetirementReceipt[],
): void {
    const projectKey = String(projectId || '').toLowerCase();
    const pending = queue.get(projectKey);
    if (!pending) return;
    for (const receipt of acknowledged) pending.delete(receiptKey(receipt));
    if (pending.size === 0) queue.delete(projectKey);
}
